import 'leaflet/dist/leaflet.css';

import { type LatLngTuple } from 'leaflet';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import styled from 'styled-components';

import {
    BASEMAPS,
    DepotMarker,
    FitBounds,
    numberedIcon,
    ROUTE_MODES,
    type Basemap,
    type RouteMode,
} from 'components/LeafletMap';
import { SegmentedControl } from 'components/SegmentedControl';
import { useTourRoutes } from 'pages/DynamicDeliveryPlanning/hooks/useTourRoutes';

import { formatDurationMinutes } from 'helpers/dateTimes';

import { GENNEVILLIERS_DEPOT, MAX_TOUR_MINUTES } from '../helpers';
import { OriginIcon } from './OriginIcon';

export type MapDelivery = {
    deliveryId: string;
    siteName: string;
    address?: string;
    details?: string;
    latitude: number;
    longitude: number;
    window?: string;
    state?: string;
    /** Microstore columns serviced at this delivery — shown for dynamic deliveries (>0). */
    columnCount?: number;
    /** On-site minutes, straight from the delivery's `theoricServiceTime` — feeds the tour load. */
    serviceMinutes: number;
    isTask?: boolean;
};

export type MapTour = {
    key: string;
    label: string;
    color: string;
    selected: boolean;
    /** Shows the dynamic bolt next to the legend label. */
    isDynamic?: boolean;
    /** Unassigned pool: plotted as scattered markers only — no ordered route line, no IGN fetch, no load. */
    markersOnly?: boolean;
    deliveries: MapDelivery[];
};

type Props = {
    tours: MapTour[];
    /** Toggle a tour in the editor selection (from its route line or legend entry). */
    onToggleTour: (key: string) => void;
    editMode?: boolean;
    insertMode?: boolean;
    selectionNumberByDeliveryId?: Map<string, number>;
    onStopClick?: (deliveryId: string) => void;
    onPickTour?: (key: string) => void;
    focusedDeliveryId?: string | null;
};

const FOCUS_ZOOM = 15;
// The legend floats over the map's right side: an opened popup pans clear of it instead of hiding under it.
const POPUP_PADDING_BOTTOM_RIGHT: [number, number] = [230, 20];

const BASEMAP_OPTIONS = BASEMAPS.map((basemap) => ({ value: basemap.id, label: basemap.label }));

const FocusStop = ({ position }: { position: LatLngTuple | null }): null => {
    const map = useMap();
    const key = position ? `${position[0]},${position[1]}` : null;
    useEffect(() => {
        if (position) map.setView(position, Math.max(map.getZoom(), FOCUS_ZOOM));
    }, [key, map]);
    return null;
};

/**
 * Lean tour map (mirrors the dynamic planning map's conventions): one polyline + numbered markers per tour,
 * tinted with the tour colour. Clicking a tour (route, marker or legend entry) toggles it in the editor
 * selection; only the unassigned pool (markersOnly) is context-only.
 */
export const TourMap = ({
    tours,
    onToggleTour,
    editMode = false,
    insertMode = false,
    selectionNumberByDeliveryId,
    onStopClick,
    onPickTour,
    focusedDeliveryId = null,
}: Props) => {
    const focusedStop = tours
        .flatMap((tour) => tour.deliveries)
        .find((delivery) => delivery.deliveryId === focusedDeliveryId);
    const picking = editMode || insertMode;
    const [routeMode, setRouteMode] = useState<RouteMode>('road');
    const [basemapId, setBasemapId] = useState<Basemap['id']>('clair');
    const basemap = BASEMAPS.find((candidate) => candidate.id === basemapId) ?? BASEMAPS[0];
    const allPositions = useMemo<LatLngTuple[]>(
        () =>
            tours.flatMap((tour) =>
                tour.deliveries.map((delivery) => [delivery.latitude, delivery.longitude] as LatLngTuple),
            ),
        [tours],
    );
    const hasSelection = tours.some((tour) => tour.selected);

    // Real road geometry per tour (IGN), only fetched in "Itinéraire" mode; falls back to straight lines otherwise.
    const tourInputs = useMemo(
        () =>
            tours
                .filter((tour) => !tour.markersOnly)
                .map((tour) => ({
                    approId: tour.key,
                    positions: tour.deliveries.map(
                        (delivery) => [delivery.latitude, delivery.longitude] as LatLngTuple,
                    ),
                })),
        [tours],
    );
    const routes = useTourRoutes(routeMode === 'road' ? tourInputs : []);
    // Fit on the tours' stops only, never on the « À affecter » pool: it can hold out-of-process points (outside
    // Île-de-France) that would zoom the map out. No tour stop → the map stays on the depot.
    const tourPositions = useMemo(() => tourInputs.flatMap(({ positions }) => positions), [tourInputs]);

    if (allPositions.length === 0) {
        return <EmptyMap>Aucun arrêt géolocalisé pour cette date.</EmptyMap>;
    }

    return (
        <MapShell>
            <MapContainer
                style={{ width: '100%', height: '100%' }}
                zoom={11}
                center={[GENNEVILLIERS_DEPOT.latitude, GENNEVILLIERS_DEPOT.longitude]}
            >
                <TileLayer
                    key={basemap.id}
                    className={basemap.pale ? 'pale-basemap' : undefined}
                    attribution={basemap.attribution}
                    url={basemap.url}
                    subdomains={basemap.subdomains}
                />
                <FitBounds points={tourPositions} once />
                <FocusStop position={focusedStop ? [focusedStop.latitude, focusedStop.longitude] : null} />
                <DepotMarker depot={GENNEVILLIERS_DEPOT} />
                {tours.map((tour) => {
                    const positions = tour.deliveries.map(
                        (delivery) => [delivery.latitude, delivery.longitude] as LatLngTuple,
                    );
                    // With an active selection, unselected tours fade out so the edited ones stand out.
                    const dimmed = hasSelection && !tour.selected;
                    // Real road geometry when loaded (Itinéraire mode), else the straight leg between deliveries.
                    const linePositions = routes.get(tour.key)?.positions ?? positions;
                    return (
                        <Fragment key={tour.key}>
                            {!tour.markersOnly && positions.length > 1 && (
                                <Polyline
                                    positions={linePositions}
                                    pathOptions={{
                                        // Selected tour: bold + fully opaque so it stands out; the others fade back.
                                        color: tour.color,
                                        weight: tour.selected ? 6 : 3,
                                        opacity: tour.selected ? 1 : dimmed ? 0.25 : 0.85,
                                    }}
                                    // Insert mode: the route line is the tour too — place the selection at its end.
                                    eventHandlers={{
                                        click: () =>
                                            insertMode && onPickTour ? onPickTour(tour.key) : onToggleTour(tour.key),
                                    }}
                                />
                            )}
                            {tour.deliveries.map((delivery, index) => {
                                const selectionNumber = selectionNumberByDeliveryId?.get(delivery.deliveryId);
                                return (
                                    <Marker
                                        key={delivery.deliveryId}
                                        position={[delivery.latitude, delivery.longitude]}
                                        icon={
                                            selectionNumber != null
                                                ? numberedIcon(tour.color, selectionNumber, { size: 22, pending: true })
                                                : numberedIcon(tour.color, index + 1, {
                                                      size: 22,
                                                      pending: delivery.deliveryId === focusedDeliveryId,
                                                  })
                                        }
                                        opacity={selectionNumber == null && dimmed ? 0.55 : 1}
                                        zIndexOffset={
                                            selectionNumber != null || delivery.deliveryId === focusedDeliveryId
                                                ? 1000
                                                : 0
                                        }
                                        eventHandlers={
                                            picking ? { click: () => onStopClick?.(delivery.deliveryId) } : {}
                                        }
                                    >
                                        {!picking && (
                                            <Popup autoPanPaddingBottomRight={POPUP_PADDING_BOTTOM_RIGHT}>
                                                <PopupBody>
                                                    <b>
                                                        {index + 1}. {delivery.siteName}
                                                    </b>
                                                    {delivery.address && <span>{delivery.address}</span>}
                                                    {delivery.details && <span>{delivery.details}</span>}
                                                    <span>
                                                        {tour.label}
                                                        {delivery.window ? ` · ${delivery.window}` : ''}
                                                        {delivery.state ? ` · ${delivery.state}` : ''}
                                                        {` · ${formatDurationMinutes(delivery.serviceMinutes)} sur place`}
                                                        {delivery.columnCount ? ` · ${delivery.columnCount} col.` : ''}
                                                    </span>
                                                    {!delivery.isTask && (
                                                        <Link to={`/delivery/${delivery.deliveryId}`}>
                                                            Fiche livraison
                                                        </Link>
                                                    )}
                                                </PopupBody>
                                            </Popup>
                                        )}
                                    </Marker>
                                );
                            })}
                        </Fragment>
                    );
                })}
            </MapContainer>

            <MapControls>
                <SegmentedControl options={BASEMAP_OPTIONS} value={basemap.id} onChange={setBasemapId} />
                <SegmentedControl options={ROUTE_MODES} value={routeMode} onChange={setRouteMode} />
            </MapControls>

            <Legend>
                <LegendTitle>Tournées</LegendTitle>
                {tours.map((tour) => {
                    // Real driving time (IGN) + the deliveries' own service time; no route ⇒ nothing to show.
                    const route = routes.get(tour.key);
                    const totalMinutes = route
                        ? route.durationSeconds / 60 +
                          tour.deliveries.reduce((sum, delivery) => sum + delivery.serviceMinutes, 0)
                        : null;
                    const selectable = !tour.markersOnly;
                    return (
                        <LegendRow
                            key={tour.key}
                            type="button"
                            $selected={tour.selected}
                            $selectable={selectable}
                            disabled={!selectable}
                            onClick={() =>
                                selectable && (insertMode && onPickTour ? onPickTour(tour.key) : onToggleTour(tour.key))
                            }
                            title={
                                insertMode
                                    ? 'Placer la sélection en fin de tournée'
                                    : selectable
                                      ? 'Sélectionner pour éditer'
                                      : 'Planifié automatiquement'
                            }
                        >
                            <Dot style={{ backgroundColor: tour.color }} />
                            <LegendText>
                                <LegendLabelRow>
                                    <LegendLabel>{tour.label}</LegendLabel>
                                    {tour.isDynamic && <OriginIcon origin="dynamique" size={12} />}
                                </LegendLabelRow>
                                {route && totalMinutes !== null && (
                                    <LegendMeta>
                                        {(route.distanceMeters / 1000).toFixed(0)} km ·{' '}
                                        {formatDurationMinutes(totalMinutes)}
                                        {totalMinutes > MAX_TOUR_MINUTES && <Overload>surchargée</Overload>}
                                    </LegendMeta>
                                )}
                            </LegendText>
                            <LegendCount>{tour.deliveries.length}</LegendCount>
                        </LegendRow>
                    );
                })}
            </Legend>
        </MapShell>
    );
};

const PopupBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    font: inherit;

    b {
        font-size: 13px;
    }
    span {
        font-size: 12px;
        color: #6b7280;
    }
    a {
        font-size: 12px;
        font-weight: 600;
    }
`;

const MapShell = styled.div`
    position: relative;
    flex: 1;
    min-width: 0;
    min-height: 420px;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    isolation: isolate;

    .leaflet-container {
        font: inherit;
    }

    /* IGN only publishes a colourful raster basemap; washed out it stops competing with the tour routes. */
    .pale-basemap {
        filter: grayscale(0.92) brightness(1.06) contrast(0.9);
    }
`;

const EmptyMap = styled.div`
    flex: 1;
    min-height: 420px;
    display: grid;
    place-items: center;
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 10px;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;

const Legend = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1000;
    max-height: 70%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    border-radius: 10px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
`;

const LegendTitle = styled.span`
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.infoTextColor};
    padding: 0 6px 4px;
`;

const LegendRow = styled.button<{ $selected: boolean; $selectable: boolean }>`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 4px 6px;
    border: none;
    border-radius: 7px;
    background: ${({ theme, $selected }) =>
        $selected ? `color-mix(in srgb, ${theme.ctaPrimaryColor} 14%, transparent)` : 'transparent'};
    cursor: ${({ $selectable }) => ($selectable ? 'pointer' : 'default')};
    opacity: ${({ $selectable }) => ($selectable ? 1 : 0.6)};

    &:hover {
        background: ${({ theme, $selectable, $selected }) =>
            $selectable && !$selected ? theme.backgroundColor : undefined};
    }
`;

const Dot = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
`;

const LegendText = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const LegendLabelRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
`;

const LegendLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
`;

const LegendMeta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
`;

const Overload = styled.span`
    font-weight: 700;
    color: #fff;
    background: ${({ theme }) => theme.warningColor};
    border-radius: 5px;
    padding: 1px 5px;
`;

const LegendCount = styled.span`
    margin-left: auto;
    padding-left: 8px;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.infoTextColor};
`;

const MapControls = styled.div`
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
`;
